import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { IItemForGetShortInfoWithStatus, UserRoles, ITotalPrice, EntityStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setRestaurantsForPrice: (value: string[]) => void;
	removePrice: (value: string[]) => void;
	selectedCustomer: Nullable<string>;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	externalId: string;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedCustomer,
	restaurantsForPrice,
	totalPrices,
	externalId,
	removePrice,
	setRestaurantsForPrice,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(
		() => [
			() => ({
				validator() {
					const IDsTotalPrices = totalPrices.map((el) => el.restaurantId);
					const isAllRestaurantsHavePrice = restaurantsForPrice.every((el) => IDsTotalPrices.includes(el));
					if (!isAllRestaurantsHavePrice) {
						return Promise.reject(new Error(t('enterPrice')));
					}
					return Promise.resolve();
				},
			}),
		],
		[totalPrices, restaurantsForPrice, t],
	);

	const visibleData = useMemo(() => {
		return showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || restaurantsForPrice.includes(el.id));
	}, [data, showArchived, restaurantsForPrice]);

	const accessibleRestaurants = useMemo(() => {
		const restaurants = loggedUser.restaurants || [];
		return showArchived
			? restaurants
			: restaurants.filter((el) => el.status === EntityStatus.Active || restaurantsForPrice.includes(el.id));
	}, [loggedUser, showArchived, restaurantsForPrice]);

	const filteredData = useMemo(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
		return filteredData.map((el) => ({
			id: el.id,
			name: el.name,
			disabled: !hasAccess && !accessibleRestaurantIds.includes(el.id),
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [filteredData, accessibleRestaurants, hasAccess]);

	// ----- where roles are used, the selection of all is prohibited ----- //
	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleData.length === restaurantsForPrice.length) {
				setRestaurantsForPrice([]);
				removePrice(restaurantsForPrice);
			} else {
				setRestaurantsForPrice(visibleData.map((el) => el.id));
			}
		} else {
			const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
			if (accessibleRestaurantIds.every((el) => restaurantsForPrice.includes(el))) {
				setRestaurantsForPrice(restaurantsForPrice.filter((el) => !accessibleRestaurantIds.includes(el)));
				removePrice(accessibleRestaurantIds);
			} else {
				const filteredAccessibleRestaurantIds = accessibleRestaurantIds.filter(
					(el) => !restaurantsForPrice.includes(el),
				);
				setRestaurantsForPrice([...restaurantsForPrice, ...filteredAccessibleRestaurantIds]);
			}
		}
	}, [hasAccess, visibleData, restaurantsForPrice, setRestaurantsForPrice, removePrice, accessibleRestaurants]);
	// ----- where roles are used, the selection of all is prohibited ----- //

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	useEffect(() => {
		setSearchString('');
	}, [selectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleData.length && visibleData.length === restaurantsForPrice.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
				return accessibleRestaurantIds.every((el) => restaurantsForPrice.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleData.length : !accessibleRestaurants.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			accessibleRestaurants,
			restaurantsForPrice,
			hasAccess,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="restaurantsForPrice"
			label="restaurantsLabel"
			options={mappedOptions}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			onChange={setRestaurantsForPrice}
			onDeselect={removePrice}
			disabled={!selectedCustomer || isLoading || !!externalId}
			validationRules={validationRules}
			dropdownRender={renderSelectMenu}
			counterValue={restaurantsForPrice.length}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
